/**
 * Subscription-access hook + gate (Feb 2026).
 *
 * The gate calls `GET /api/subscription/access` after auth resolves and
 * decides whether to render children, a redirect, or a banner. The backend
 * enforces the same rules on every protected API — the frontend gate is
 * only for UX. Users can never bypass by URL manipulation.
 *
 * Access modes returned by the backend:
 *   full       → render app normally
 *   read_only  → render app + top banner ("grace period")
 *   past_due   → render app + top banner ("billing issue")
 *   pending    → redirect to /complete-subscription
 *   archived   → redirect to /subscription-archived
 */
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache the last resolved access per-session in memory so tab-navigation
// doesn't trigger a fresh network hit on every route change. AuthContext
// resets on logout, so we tie invalidation to `isAuthenticated`.
let _memo = { user_id: null, data: null };

export function useSubscriptionAccess() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      _memo = { user_id: null, data: null };
      setState({ loading: false, data: null, error: null });
      return;
    }
    if (_memo.user_id === user?.user_id && _memo.data) {
      setState({ loading: false, data: _memo.data, error: null });
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/subscription/access`, { credentials: 'include' });
        if (!r.ok) {
          if (!cancelled) setState({ loading: false, data: null, error: `HTTP ${r.status}` });
          return;
        }
        const data = await r.json();
        _memo = { user_id: user?.user_id, data };
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: 'network' });
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.user_id]);

  return {
    loading: authLoading || state.loading,
    access: state.data,
    error: state.error,
    // Convenience flags for UI.
    isFull:     state.data?.mode === 'full',
    isPending:  state.data?.mode === 'pending',
    isPastDue:  state.data?.mode === 'past_due',
    isReadOnly: state.data?.mode === 'read_only',
    isArchived: state.data?.mode === 'archived',
    isSuspended: state.data?.mode === 'suspended',
    canWrite:   !!state.data?.can_write,
    canRead:    !!state.data?.can_read,
  };
}

// Invalidate the cached access snapshot. Call from any UI that changes the
// subscription (Platform Manager editor, checkout completion) so the next
// render re-fetches. Also called when the user logs out.
export function invalidateSubscriptionAccess() {
  _memo = { user_id: null, data: null };
}

// Paths that MUST remain reachable regardless of subscription state so the
// user can log out, complete payment or read explanatory pages.
const ALLOWLIST_PATHS = [
  '/complete-subscription',
  '/billing-issue',
  '/subscription-archived',
  '/checkout',
  '/billing/success',
  '/pricing',
];

export function SubscriptionGate({ children }) {
  const { loading, access, isFull, isPending, isPastDue, isReadOnly, isArchived, isSuspended } = useSubscriptionAccess();
  const { user, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-[#64748B]">Checking subscription…</p>
        </div>
      </div>
    );
  }

  // Internal roles (platform_admin, qa) bypass the gate entirely — they
  // operate above/across tenants and don't hold a subscription. The backend
  // returns `is_internal: true` on their access record (and `mode: "full"`),
  // so we let them through even if the record hasn't loaded yet. This does
  // NOT grant them any additional permissions or navigation — only skips
  // the subscription redirect. Kept the legacy `is_platform_admin` check
  // for backwards compatibility with older responses.
  const roleLc = (user?.role || '').toLowerCase();
  if (access?.is_internal || access?.is_platform_admin || roleLc === 'platform_admin' || roleLc === 'qa') {
    return children;
  }

  // Manual organisation suspension — full-screen notice for the whole app
  // shell. Login succeeds, but the tenant's users see only this message.
  // Platform admins/QA have already returned above, so they are unaffected.
  if (isSuspended) {
    return <SuspendedNotice onSignOut={logout} />;
  }

  const path = location.pathname || '/';
  const onAllowlisted = ALLOWLIST_PATHS.some(p => path === p || path.startsWith(`${p}/`));

  if (isPending && !onAllowlisted) {
    return <Navigate to="/complete-subscription" replace />;
  }
  if (isArchived && !onAllowlisted) {
    return <Navigate to="/subscription-archived" replace />;
  }
  // past_due and read_only still render the app; a top banner (rendered
  // inside DashboardLayout) tells the user what's happening.
  if (isFull || isPastDue || isReadOnly || onAllowlisted) {
    return children;
  }
  // Unknown state — fail closed to the pending landing.
  return <Navigate to="/complete-subscription" replace />;
}

// Full-screen suspension notice shown to a suspended client's users.
function SuspendedNotice({ onSignOut }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F2856] px-6" data-testid="org-suspended-notice">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-[#FEE2E2] flex items-center justify-center">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
          </svg>
        </div>
        <h1 className="font-['Montserrat'] text-xl font-bold text-[#0F172A]">Access suspended</h1>
        <p className="mt-3 text-sm text-[#475569] leading-relaxed" data-testid="org-suspended-message">
          Your organisation's access has been suspended. Please contact satisFIND at{' '}
          <a href="mailto:hello@satisfind.com" className="text-[#1A3E6F] font-semibold hover:underline">hello@satisfind.com</a>{' '}
          for further details.
        </p>
        <button
          onClick={onSignOut}
          className="mt-7 w-full bg-[#1A3E6F] hover:bg-[#12305A] text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
          data-testid="org-suspended-signout"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
